import { useMutation, useQuery } from '@apollo/client';
import { BellAlertIcon, BellIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { PortfolioUpdateRequestStatusEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';

import PostCard from 'components/PostCard';

import Button from 'primitives/Button';
import EmptyState from 'primitives/EmptyState';
import LoadingIndicator from 'primitives/LoadingIndicator';

import PostSlideOver from '../../Posts/SyndicatePosts/PostSlideOver';

const DEAL_UPDATES_QUERY = gql(`
  query DealUpdatesTab($dealId: ID!) {
    recentDealPosts(dealId: $dealId, limit: 20) {
      id
      title
      status
      message
      createdAt
      publishedAt
      sender {
        id
        name
      }
      syndicate {
        id
        name
        image
      }
    }
    myPortfolioUpdateRequest(dealId: $dealId) {
      id
      status
      leadNote
      createdAt
    }
  }
`);

const CREATE_REQUEST_MUTATION = gql(`
  mutation CreatePortfolioUpdateRequestOnDealPage($dealId: ID!) {
    createPortfolioUpdateRequest(dealId: $dealId) {
      id
      createdAt
    }
  }
`);

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const now = new Date();
  const past = new Date(dateStr);
  const diffMs = now.getTime() - past.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

type RequestBannerProps = {
  status: PortfolioUpdateRequestStatusEnumType;
  leadNote?: string | null;
  createdAt: string;
};

const REQUEST_CONFIG: Record<
  PortfolioUpdateRequestStatusEnumType,
  {
    icon: typeof BellIcon;
    iconBg: string;
    iconColor: string;
    title: (t: string) => string;
    body: string;
  }
> = {
  [PortfolioUpdateRequestStatusEnumType.Pending]: {
    icon: BellAlertIcon,
    iconBg: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
    title: t => `Update requested ${t}`,
    body: 'The syndicate lead has been notified. You can request another update in 3 months.',
  },
  [PortfolioUpdateRequestStatusEnumType.Acknowledged]: {
    icon: BellAlertIcon,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    title: t => `Update requested ${t}`,
    body: 'The syndicate lead has acknowledged your request and is working on an update.',
  },
  [PortfolioUpdateRequestStatusEnumType.Fulfilled]: {
    icon: CheckCircleIcon,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    title: t => `Update fulfilled ${t}`,
    body: 'The syndicate lead has responded. See the latest post below.',
  },
  [PortfolioUpdateRequestStatusEnumType.Archived]: {
    icon: XCircleIcon,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-500',
    title: t => `Request archived ${t}`,
    body: 'The syndicate lead has archived this request.',
  },
};

const RequestBanner: FC<RequestBannerProps> = ({ status, leadNote, createdAt }) => {
  const config = REQUEST_CONFIG[status];
  const Icon = config.icon;
  const isArchived = status === PortfolioUpdateRequestStatusEnumType.Archived;

  return (
    <div
      className={`rounded-xl border p-5 ${
        isArchived ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 rounded-lg p-2 ${config.iconBg}`}>
          <Icon className={`h-5 w-5 ${config.iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{config.title(timeAgo(createdAt))}</p>
          <p className="mt-0.5 text-sm text-gray-500">{config.body}</p>
          {isArchived && leadNote && (
            <div className="mt-3 rounded-md bg-white border border-gray-200 px-3 py-2">
              <p className="text-xs font-medium text-gray-400 mb-0.5">Lead's note</p>
              <p className="text-sm text-gray-700">{leadNote}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DealUpdates: FC<{ dealId: string }> = ({ dealId }) => {
  const [postOnDisplay, setPostOnDisplay] = useState('');

  const { loading, data, refetch } = useQuery(DEAL_UPDATES_QUERY, {
    variables: { dealId },
  });

  const [createRequest, { loading: requesting, error: requestError }] = useMutation(
    CREATE_REQUEST_MUTATION,
    {
      onCompleted() {
        refetch();
      },
    }
  );

  if (loading) return <LoadingIndicator />;

  const posts = data?.recentDealPosts ?? [];
  const existingRequest = data?.myPortfolioUpdateRequest;
  const canRequest = !existingRequest;

  return (
    <>
      <PostSlideOver
        open={!!postOnDisplay}
        postId={postOnDisplay}
        onClose={() => setPostOnDisplay('')}
      />

      <div className="space-y-6">
        {/* {existingRequest ? (
          <RequestBanner
            status={existingRequest.status}
            leadNote={existingRequest.leadNote}
            createdAt={existingRequest.createdAt}
          />
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0 rounded-lg p-2 bg-indigo-50">
                  <BellIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Request a portfolio update</p>
                  <p className="mt-0.5 text-sm text-gray-500">
                    Ask the syndicate lead to share the latest metrics and progress for this
                    company. They'll be notified by email.
                  </p>
                  {requestError && (
                    <p className="mt-1 text-xs text-red-600">{requestError.message}</p>
                  )}
                </div>
              </div>
              {canRequest && (
                <div className="flex-shrink-0">
                  <Button
                    loading={requesting}
                    onClick={() => createRequest({ variables: { dealId } })}
                  >
                    Request Update
                  </Button>
                </div>
              )}
            </div>
          </div>
        )} */}

        {posts.length === 0 ? (
          <EmptyState
            title="No updates yet"
            description="Updates shared by the syndicate lead will appear here."
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Updates from this syndicate
            </h2>
            <div className="space-y-2">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  id={post.id}
                  title={post.title}
                  message={post.message}
                  createdAt={post.createdAt}
                  publishedAt={post.publishedAt}
                  sender={post.sender}
                  syndicate={post.syndicate}
                  onClick={id => setPostOnDisplay(id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DealUpdates;
